import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthService, RoutingService, StateUtils, StateWithProcess } from '@spartacus/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, map, share, switchMap, take, tap } from 'rxjs/operators';
import { UserIdService } from '../../auth';
import { ArticleRef, ArticleStockInfo, LoaderError, StockInfoDetails, Unit } from '../../model';
import { withdrawOn } from '../../util';
import { StockInfoFacade } from '../facades';
import { UserActions } from '../store/actions';
import {
  getStockInfoDetailsLoaderState,
  getStockInfoDetailsStateError,
  getStockInfoDetailsStateLoading,
} from '../store/selectors/stock-info-details.selectors';
import {
  getStockInfoLoaderState,
  getStockInfoStateError,
  getStockInfoStateLoading,
} from '../store/selectors/stock-info.selectors';
import { StateWithUser } from '../store/user-state';

@Injectable()
export class StockInfoService implements StockInfoFacade {
  constructor(
    private store: Store<StateWithUser & StateWithProcess<void>>,
    private authService: AuthService,
    private routingService: RoutingService,
    private userIdService: UserIdService
  ) {}

  getStockInfo(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): Observable<ArticleStockInfo> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());
    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() =>
        this.store.select(getStockInfoLoaderState, { key: this.getEntityKey(articleRef, quantity, customerSelectedUnit) })
      ),
      tap((loaderState) => {
        if (this.needsToLoad(loaderState)) {
          this.loadStockInfo(articleRef, quantity, customerSelectedUnit);
        }
      }),
      filter((loaderState) => loaderState.success && !!loaderState.value),
      map((loaderState) => loaderState.value.stockInfo),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn))),
      take(1)
    );
  }

  getStockInfoDetails(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): Observable<StockInfoDetails> {
    const eCommerceQuantity = this.calculateECommerceQuantity(quantity, customerSelectedUnit);
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());
    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(getStockInfoDetailsLoaderState, { key: `${articleRef}|${eCommerceQuantity}` })),
      tap((loaderState) => {
        if (this.needsToLoad(loaderState)) {
          this.loadStockInfoDetails(articleRef, quantity, customerSelectedUnit);
        }
      }),
      filter((loaderState) => loaderState.success && !!loaderState.value),
      map((loaderState) => loaderState.value.stockInfo),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn))),
      take(1)
    );
  }

  loadingStockInfo(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): Observable<boolean> {
    return this.store.select(getStockInfoStateLoading, { key: this.getEntityKey(articleRef, quantity, customerSelectedUnit) });
  }

  getStockInfoError(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): Observable<LoaderError> {
    return this.store.select(getStockInfoStateError, { key: this.getEntityKey(articleRef, quantity, customerSelectedUnit) });
  }

  loadStockInfo(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): void {
    this.userIdService
      .takeUserId(true)
      .pipe(
        tap((userId) => {
          this.store.dispatch(
            new UserActions.LoadStockInfo({
              articleNumber: articleRef,
              quantity: quantity,
              unit: customerSelectedUnit?.code,
              userId,
            })
          );
        }),
        catchError(() => {
          this.routingService.go({ cxRoute: 'login' });
          return EMPTY;
        })
      )
      .subscribe();
  }

  loadingStockInfoDetails(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): Observable<boolean> {
    const eCommerceQuantity = this.calculateECommerceQuantity(quantity, customerSelectedUnit);
    return this.store.select(getStockInfoDetailsStateLoading, { key: `${articleRef}|${eCommerceQuantity}` });
  }

  getStockInfoDetailsError(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): Observable<LoaderError> {
    const eCommerceQuantity = this.calculateECommerceQuantity(quantity, customerSelectedUnit);
    return this.store.select(getStockInfoDetailsStateError, { key: `${articleRef}|${eCommerceQuantity}` });
  }

  loadStockInfoDetails(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit) {
    this.userIdService
      .takeUserId(true)
      .pipe(
        tap((userId) => {
          this.store.dispatch(
            new UserActions.LoadStockInfoDetails({
              articleNumber: articleRef,
              quantity: !!customerSelectedUnit ? customerSelectedUnit.inEcommerceUnit * quantity : quantity,
              userId,
            })
          );
        }),
        catchError(() => {
          this.routingService.go({ cxRoute: 'login' });
          return EMPTY;
        })
      )
      .subscribe();
  }

  private getEntityKey(articleRef: ArticleRef, quantity: number, customerSelectedUnit?: Unit): string {
    return `${articleRef}|${quantity}|${customerSelectedUnit?.code ?? 'x'}`;
  }

  private needsToLoad(loaderState: StateUtils.LoaderState<any>) {
    if (
      !!loaderState.value &&
      !!loaderState.value.lastUpdateTime &&
      new Date(loaderState.value.lastUpdateTime).toDateString() !== new Date().toDateString()
    ) {
      return true;
    }
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }

  private calculateECommerceQuantity(quantity: number, customerSelectedUnit?: Unit): number {
    return !!customerSelectedUnit ? customerSelectedUnit.inEcommerceUnit * quantity : quantity;
  }
}
